import { useNavigate, useParams, useSearch } from '@tanstack/react-router';
import { useState } from 'react';
import { useOpenReport } from '@/api/rpc/hooks/apps/useApps';
import { Skeleton } from '@/components/ui/skeleton';
import { HeaderPageTitle } from '@/context/header-title.context';
import { inputsOrEmpty, loadSavedInputs, saveInputs } from './reportState';
import { ReportView } from './ReportView';

/**
 * A Report App: its view over feeds read as the viewer. Filters are view
 * state; changing them reads the feeds again and patches the view in place.
 */
export function ReportPage() {
  const { appId } = useParams({ from: '/authenticated/reports/$appId' });
  // Navigating from one Report to another reuses this page: key by Report so
  // the opening inputs and view state are read again.
  return <ReportPageFor key={appId} appId={appId} />;
}

function ReportPageFor({ appId }: { appId: string }) {
  const search = useSearch({ from: '/authenticated/reports/$appId' });
  // The URL wins over this browser's last filters; read once per open.
  const [initialInputs] = useState(
    () => search.inputs ?? inputsOrEmpty(loadSavedInputs(appId)),
  );
  const opened = useOpenReport(appId, initialInputs);
  const navigate = useNavigate({ from: '/reports/$appId' });
  // Filters travel in the URL (to share) and stay in this browser (to return to).
  const keepInputs = (inputs: Record<string, unknown>) => {
    saveInputs(appId, inputs);
    void navigate({ search: { inputs }, replace: true });
  };

  return (
    <div className="mx-auto w-full max-w-6xl space-y-3 p-4">
      {opened.error ? (
        <div className="rounded-md border border-destructive/40 bg-destructive/5 px-3 py-2 text-sm text-destructive">
          {opened.error.message}
        </div>
      ) : opened.data ? (
        <>
          <HeaderPageTitle>{opened.data.app.name}</HeaderPageTitle>
          <ReportView
            key={opened.data.deployment.id}
            opened={opened.data}
            initialInputs={initialInputs}
            onInputsChange={keepInputs}
          />
        </>
      ) : (
        <div className="space-y-2">
          <Skeleton className="h-8 w-1/3" />
          <Skeleton className="h-40 w-full" />
        </div>
      )}
    </div>
  );
}
